<template>
  <div class="detail-drawer-overlay" @click.self="requestClose">
    <aside ref="drawer" class="detail-drawer" tabindex="-1" role="dialog" aria-modal="true">
      <header class="drawer-header">
        <div>
          <p class="drawer-label">{{ $t('employeeRegistrationKpi.detail.header_label') }}</p>
          <h5>{{ employeeCode || placeholder }} · {{ employeeName || placeholder }}</h5>
          <p class="drawer-branch">{{ branchName || placeholder }}</p>
        </div>
        <div class="drawer-actions">
          <vs-button
              flat
              class="form-btn-loading"
              :loading="exporting"
              :disabled="exporting"
              @click="exportDetail"
          >
            {{ $t('employeeRegistrationKpi.detail.export_button') }}
          </vs-button>
          <button type="button" class="drawer-close" @click="requestClose" aria-label="Close">
            <i class="fal fa-times"></i>
          </button>
        </div>
      </header>

      <section class="drawer-filters">
        <div class="filter-block filter-toggle">
          <label class="label">{{ $t('employeeRegistrationKpi.detail.filters.used_app') }}:</label>
          <div class="segmented-control">
            <button
                type="button"
                :class="{ active: filters.used_app === null }"
                @click="setUsedApp(null)"
            >
              {{ $t('employeeRegistrationKpi.detail.filters.all') }}
            </button>
            <button
                type="button"
                :class="{ active: filters.used_app === true }"
                @click="setUsedApp(true)"
            >
              {{ $t('employeeRegistrationKpi.detail.filters.used') }}
            </button>
            <button
                type="button"
                :class="{ active: filters.used_app === false }"
                @click="setUsedApp(false)"
            >
              {{ $t('employeeRegistrationKpi.detail.filters.never_used') }}
            </button>
          </div>
        </div>

        <div class="filter-block">
          <label class="label">{{ $t('employeeRegistrationKpi.detail.filters.search') }}:</label>
          <input
              v-model="searchTerm"
              class="input"
              type="text"
              :placeholder="$t('employeeRegistrationKpi.detail.filters.search_placeholder')"
              @input="onSearchInput"
          />
        </div>

        <div class="filter-block date-filter">
          <label class="label">{{ $t('employeeRegistrationKpi.detail.filters.from') }}:</label>
          <date-picker
              v-model="filters.from"
              format="DD/MM/YYYY"
              value-type="YYYY-MM-DD"
              type="date"
              name="detail_from"
              @change="onFilterChange"
          ></date-picker>
        </div>

        <div class="filter-block date-filter">
          <label class="label">{{ $t('employeeRegistrationKpi.detail.filters.to') }}:</label>
          <date-picker
              v-model="filters.to"
              format="DD/MM/YYYY"
              value-type="YYYY-MM-DD"
              type="date"
              name="detail_to"
              @change="onFilterChange"
          ></date-picker>
        </div>
      </section>

      <section class="drawer-content">
        <div v-if="loading" class="state-panel">
          <Loading/>
          <p>{{ $t('employeeRegistrationKpi.detail.loading') }}</p>
        </div>

        <div v-else-if="errorMessage" class="state-panel error-panel">
          <p>{{ errorMessage }}</p>
          <vs-button flat danger @click="retryLoad">
            {{ $t('employeeRegistrationKpi.detail.retry') }}
          </vs-button>
        </div>

        <div v-else-if="rows.length === 0" class="state-panel">
          <p>{{ $t('employeeRegistrationKpi.detail.empty_state') }}</p>
        </div>

        <vs-table v-else>
          <template #thead>
            <vs-tr>
              <vs-th>{{ $t('employeeRegistrationKpi.detail.columns.customer_id') }}</vs-th>
              <vs-th>{{ $t('employeeRegistrationKpi.detail.columns.customer_name') }}</vs-th>
              <vs-th>{{ $t('employeeRegistrationKpi.detail.columns.tel') }}</vs-th>
              <vs-th>{{ $t('employeeRegistrationKpi.detail.columns.registered_at') }}</vs-th>
              <vs-th>{{ $t('employeeRegistrationKpi.detail.columns.last_app_used_at') }}</vs-th>
              <vs-th>{{ $t('employeeRegistrationKpi.detail.columns.used_app') }}</vs-th>
            </vs-tr>
          </template>
          <template #tbody>
            <vs-tr v-for="row in rows" :key="row.customer_id">
              <vs-td>{{ row.customer_id || placeholder }}</vs-td>
              <vs-td>{{ fullName(row) }}</vs-td>
              <vs-td>{{ row.tel || placeholder }}</vs-td>
              <vs-td>{{ formatDateTime(row.registered_at) }}</vs-td>
              <vs-td>{{ row.last_app_used_at ? formatDateTime(row.last_app_used_at) : placeholder }}</vs-td>
              <vs-td>
                <span :class="['used-app-badge', row.has_used_app ? 'is-used' : 'is-never-used']">
                  {{ row.has_used_app ? $t('employeeRegistrationKpi.detail.badges.yes') : $t('employeeRegistrationKpi.detail.badges.no') }}
                </span>
              </vs-td>
            </vs-tr>
          </template>
          <template #footer>
            <pagination
                v-if="paginator && paginator.last_page > 1"
                :pagination="paginator"
                :offset="8"
                @paginate="changePage"
            ></pagination>
          </template>
        </vs-table>
      </section>
    </aside>
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import Loading from '../../Loading';
import pagination from '../../../Plugins/Paginations/Pagination';
import {
  downloadKpiAllDetailExport,
  fetchKpiAllDetail
} from '@/services/employeeRegistrationKpiService';

export default {
  components: {
    DatePicker,
    Loading,
    pagination
  },
  props: {
    employeeId: {
      type: [Number, String],
      required: true
    },
    employeeCode: {
      type: String,
      default: null
    },
    employeeName: {
      type: String,
      default: ''
    },
    branchName: {
      type: String,
      default: ''
    }
  },
  data() {
    const today = new Date().toISOString().slice(0, 10);
    return {
      rows: [],
      paginator: null,
      filters: {
        from: today,
        to: today,
        used_app: null
      },
      perPage: 20,
      currentPage: 1,
      searchTerm: '',
      debouncedSearchTerm: '',
      searchDebounceTimer: null,
      loading: false,
      exporting: false,
      errorMessage: '',
      detailRequestId: 0
    };
  },
  computed: {
    placeholder() {
      return this.$t('employeeRegistrationKpi.placeholder');
    }
  },
  watch: {
    employeeId: {
      immediate: true,
      handler() {
        this.resetState();
        this.$nextTick(() => {
          if (this.$refs.drawer && typeof this.$refs.drawer.focus === 'function') {
            this.$refs.drawer.focus();
          }
          this.loadDetails(1);
        });
      }
    }
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown);
    clearTimeout(this.searchDebounceTimer);
  },
  methods: {
    resetState() {
      clearTimeout(this.searchDebounceTimer);
      const today = new Date().toISOString().slice(0, 10);
      this.rows = [];
      this.paginator = null;
      this.filters = {
        from: today,
        to: today,
        used_app: null
      };
      this.currentPage = 1;
      this.searchTerm = '';
      this.debouncedSearchTerm = '';
      this.errorMessage = '';
      this.loading = false;
      this.exporting = false;
    },
    buildParams(page = this.currentPage) {
      const params = {
        from: this.filters.from || undefined,
        to: this.filters.to || undefined,
        q: this.debouncedSearchTerm.trim() || undefined,
        per_page: this.perPage,
        page
      };

      if (this.filters.used_app !== null) {
        params.used_app = this.filters.used_app;
      }

      return params;
    },
    buildExportParams() {
      const params = {
        from: this.filters.from || undefined,
        to: this.filters.to || undefined,
        q: this.debouncedSearchTerm.trim() || undefined
      };

      if (this.filters.used_app !== null) {
        params.used_app = this.filters.used_app;
      }

      return params;
    },
    async loadDetails(page = 1) {
      const requestId = this.detailRequestId + 1;
      this.detailRequestId = requestId;
      this.currentPage = page;
      this.loading = true;
      this.errorMessage = '';

      try {
        const response = await fetchKpiAllDetail(this.employeeId, this.buildParams(page));
        if (requestId !== this.detailRequestId) {
          return;
        }
        this.rows = this.getResponseRows(response);
        this.paginator = this.getResponsePaginator(response);
      } catch (e) {
        if (requestId === this.detailRequestId) {
          this.rows = [];
          this.paginator = null;
          this.errorMessage = this.$t('employeeRegistrationKpi.detail.error_loading');
        }
      } finally {
        if (requestId === this.detailRequestId) {
          this.loading = false;
        }
      }
    },
    getResponseRows(response) {
      if (Array.isArray(response)) {
        return response;
      }

      if (response && Array.isArray(response.data)) {
        return response.data;
      }

      if (response && response.data && Array.isArray(response.data.data)) {
        return response.data.data;
      }

      return [];
    },
    getResponsePaginator(response) {
      if (Array.isArray(response)) {
        return null;
      }

      if (response && response.data && Array.isArray(response.data.data)) {
        return response.data;
      }

      return response;
    },
    setUsedApp(value) {
      if (this.filters.used_app === value) {
        return;
      }

      this.filters.used_app = value;
      this.onFilterChange();
    },
    onFilterChange() {
      this.currentPage = 1;
      this.loadDetails(1);
    },
    onSearchInput() {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.debouncedSearchTerm = this.searchTerm;
        this.onFilterChange();
      }, 300);
    },
    retryLoad() {
      this.loadDetails(this.currentPage || 1);
    },
    changePage(page) {
      this.loadDetails(page);
    },
    fullName(row) {
      const name = [row.name, row.surname].filter(Boolean).join(' ');
      return name || this.placeholder;
    },
    formatDateTime(value) {
      if (!value) {
        return this.placeholder;
      }

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }

      return date.toLocaleString();
    },
    async exportDetail() {
      if (this.exporting) {
        return;
      }

      this.exporting = true;
      try {
        const {blob, filename} = await downloadKpiAllDetailExport(this.employeeId, this.buildExportParams());
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (e) {
        this.$vs.notification({
          color: 'danger',
          position: 'top-right',
          title: this.$t('employeeRegistrationKpi.detail.error_toast_export_failed'),
          text: this.$t('employeeRegistrationKpi.detail.error_toast_export_failed')
        });
      } finally {
        this.exporting = false;
      }
    },
    onKeydown(event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        this.requestClose();
      }
    },
    requestClose() {
      this.$emit('close');
    }
  }
};
</script>

<style lang="scss" scoped>
.detail-drawer-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  justify-content: flex-end;
  background: rgba(10, 20, 40, 0.36);
}

.detail-drawer {
  width: 720px;
  max-width: 90vw;
  height: 100vh;
  background: #fff;
  box-shadow: -8px 0 24px rgba(10, 20, 40, 0.18);
  display: flex;
  flex-direction: column;
  outline: none;
}

.drawer-header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px;
  border-bottom: 1px solid #e8e8e8;
}

.drawer-label {
  margin-bottom: 4px;
  color: #6b7280;
  font-size: 13px;
}

.drawer-header h5 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
}

.drawer-branch {
  margin: 4px 0 0;
  color: #4b5563;
}

.drawer-actions {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.drawer-close {
  width: 36px;
  height: 36px;
  border: 1px solid #d8d8d8;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
}

.drawer-filters {
  display: grid;
  grid-template-columns: minmax(220px, 1.4fr) minmax(180px, 1fr) minmax(130px, 0.7fr) minmax(130px, 0.7fr);
  gap: 12px;
  padding: 14px 20px;
  border-bottom: 1px solid #e8e8e8;
}

.filter-block {
  min-width: 0;
}

.segmented-control {
  display: flex;
  border: 1px solid #d8d8d8;
  border-radius: 6px;
  overflow: hidden;
}

.segmented-control button {
  flex: 1;
  min-height: 38px;
  border: 0;
  border-right: 1px solid #d8d8d8;
  background: #fff;
  cursor: pointer;
  font-size: 13px;
}

.segmented-control button:last-child {
  border-right: 0;
}

.segmented-control button.active {
  background: #195bff;
  color: #fff;
}

.drawer-content {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 16px 20px 24px;
}

.state-panel {
  min-height: 180px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  color: #555;
}

.error-panel {
  border-color: #f4b5b5;
  color: #b42318;
}

.used-app-badge {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 0 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.used-app-badge.is-used {
  color: #047857;
  background: #d1fae5;
}

.used-app-badge.is-never-used {
  color: #4b5563;
  background: #e5e7eb;
}

@media (max-width: 900px) {
  .drawer-filters {
    grid-template-columns: 1fr 1fr;
  }

  .filter-toggle {
    grid-column: span 2;
  }
}

@media (max-width: 560px) {
  .drawer-header {
    flex-direction: column;
  }

  .drawer-filters {
    grid-template-columns: 1fr;
  }

  .filter-toggle {
    grid-column: span 1;
  }
}
</style>
